import React, { useState } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  BlockStack,
  InlineStack,
  TextContainer,
  Badge,
  Banner,
  ButtonGroup,
  Divider,
  Modal,
} from '@shopify/polaris';
import { DeleteMinor, PlusMinor, MagicMinor } from '@shopify/polaris-icons';
import { AIMessageGenerator } from './AIMessageGenerator';

type MessageType = 'list' | 'buttons' | 'cta' | 'product' | 'product_list' | 'flow';

interface InteractiveMessageBuilderProps {
  onSend: (messageData: any) => Promise<void>;
  customerPhone?: string;
  conversationId?: string;
  products?: any[];
}

export const InteractiveMessageBuilder: React.FC<InteractiveMessageBuilderProps> = ({
  onSend,
  customerPhone,
  conversationId,
  products = [],
}) => {
  const [messageType, setMessageType] = useState<MessageType>('buttons');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // AI Features state
  const [aiGeneratorModalActive, setAiGeneratorModalActive] = useState(false);
  const [aiSuggestionLoading, setAiSuggestionLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<{
    suggestedType: string;
    confidence: number;
    reasoning: string;
  } | null>(null);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);

  // Common fields
  const [to, setTo] = useState(customerPhone || '');
  const [header, setHeader] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');

  // List message fields
  const [buttonText, setButtonText] = useState('View Options');
  const [sections, setSections] = useState([
    {
      title: 'Section 1',
      rows: [{ id: 'option_1', title: 'Option 1', description: '' }],
    },
  ]);

  // Button fields
  const [buttons, setButtons] = useState([
    { id: 'btn_1', title: 'Option 1' },
  ]);

  // CTA fields
  const [ctaButtons, setCtaButtons] = useState([
    { type: 'url' as 'url' | 'phone_number', text: 'Visit Website', url: '', phone_number: '' },
  ]);

  // Product fields
  const [catalogId, setCatalogId] = useState('');
  const [productRetailerId, setProductRetailerId] = useState('');
  const [productSections, setProductSections] = useState([
    {
      title: 'Products',
      product_items: [{ product_retailer_id: '' }],
    },
  ]);

  // Flow fields
  const [flowId, setFlowId] = useState('');
  const [flowCta, setFlowCta] = useState('Start');

  const messageTypeOptions = [
    { label: 'Reply Buttons', value: 'buttons' },
    { label: 'List Menu', value: 'list' },
    { label: 'Call-to-Action', value: 'cta' },
    { label: 'Single Product', value: 'product' },
    { label: 'Product List', value: 'product_list' },
    { label: 'Flow Form', value: 'flow' },
  ];

  // AI: Get message type suggestion
  const getAiSuggestion = async () => {
    if (!conversationId) {
      setError('Conversation ID required for AI suggestions');
      return;
    }

    setAiSuggestionLoading(true);
    setError('');

    try {
      const response = await fetch('/api/ai-interactive/suggest-message-type', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          intent: body || 'General message',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get AI suggestion');
      }

      const data = await response.json();
      setAiSuggestion(data.data);
      setShowAiSuggestion(true);
    } catch (err: any) {
      setError(err.message || 'Failed to get AI suggestion');
    } finally {
      setAiSuggestionLoading(false);
    }
  };

  // AI: Apply suggested message type
  const applySuggestion = () => {
    if (aiSuggestion) {
      setMessageType(aiSuggestion.suggestedType as MessageType);
      setShowAiSuggestion(false);
    }
  };

  // AI: Handle generated message from AI Generator
  const handleAiGeneratedMessage = (message: any, type: string) => {
    // Populate form with AI-generated content
    setMessageType(type as MessageType);
    setHeader(message.header || '');
    setBody(message.body || '');
    setFooter(message.footer || '');

    if (type === 'buttons' && message.buttons) {
      setButtons(message.buttons);
    } else if (type === 'list' && message.sections) {
      setButtonText(message.buttonText || 'View Options');
      setSections(message.sections);
    }

    setAiGeneratorModalActive(false);
  };

  const handleSend = async () => {
    setError('');
    setLoading(true);

    try {
      let messageData: any = {
        to,
        header: header || undefined,
        body,
        footer: footer || undefined,
      };

      switch (messageType) {
        case 'list':
          messageData = {
            ...messageData,
            buttonText,
            sections: sections.map(s => ({
              title: s.title,
              rows: s.rows.filter(r => r.title && r.id),
            })),
          };
          break;

        case 'buttons':
          messageData = {
            ...messageData,
            buttons: buttons.filter(b => b.title && b.id),
          };
          break;

        case 'cta':
          messageData = {
            ...messageData,
            buttons: ctaButtons.filter(b => b.text && (b.url || b.phone_number)),
          };
          break;

        case 'product':
          messageData = {
            ...messageData,
            catalogId,
            productRetailerId,
          };
          break;

        case 'product_list':
          messageData = {
            ...messageData,
            catalogId,
            sections: productSections.map(s => ({
              title: s.title,
              product_items: s.product_items.filter(p => p.product_retailer_id),
            })),
          };
          break;

        case 'flow':
          messageData = {
            ...messageData,
            flowId,
            flowCta,
          };
          break;
      }

      await onSend(messageData);
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    setSections([...sections, { title: `Section ${sections.length + 1}`, rows: [{ id: '', title: '', description: '' }] }]);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const addRow = (sectionIndex: number) => {
    const newSections = [...sections];
    newSections[sectionIndex].rows.push({ id: '', title: '', description: '' });
    setSections(newSections);
  };

  const updateRow = (sectionIndex: number, rowIndex: number, field: string, value: string) => {
    const newSections = [...sections];
    newSections[sectionIndex].rows[rowIndex] = {
      ...newSections[sectionIndex].rows[rowIndex],
      [field]: value,
    };
    setSections(newSections);
  };

  const addButton = () => {
    if (buttons.length < 3) {
      setButtons([...buttons, { id: `btn_${buttons.length + 1}`, title: '' }]);
    }
  };

  const removeButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const updateButton = (index: number, field: string, value: string) => {
    const newButtons = [...buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setButtons(newButtons);
  };

  const addCtaButton = () => {
    if (ctaButtons.length < 2) {
      setCtaButtons([...ctaButtons, { type: 'url', text: '', url: '', phone_number: '' }]);
    }
  };

  const removeCtaButton = (index: number) => {
    setCtaButtons(ctaButtons.filter((_, i) => i !== index));
  };

  const updateCtaButton = (index: number, field: string, value: any) => {
    const newButtons = [...ctaButtons];
    newButtons[index] = { ...newButtons[index], [field]: value };
    setCtaButtons(newButtons);
  };

  const renderMessageTypeFields = () => {
    switch (messageType) {
      case 'list':
        return (
          <>
            <TextField
              label="Button Text"
              value={buttonText}
              onChange={setButtonText}
              placeholder="View Options"
              helpText="Text shown on the list button"
              autoComplete="off"
            />

            <div>
              <InlineStack gap="400" align="center">
                <TextContainer>
                  <p style={{ fontWeight: 'bold' }}>Sections & Options</p>
                </TextContainer>
                <Button onClick={addSection} icon={PlusMinor}>Add Section</Button>
              </InlineStack>

              {sections.map((section, sIndex) => (
                <Card key={sIndex}>
                  <InlineStack gap="400">
                    <TextField
                      label={`Section ${sIndex + 1} Title`}
                      value={section.title}
                      autoComplete="off"
                      onChange={(value) => {
                        const newSections = [...sections];
                        newSections[sIndex].title = value;
                        setSections(newSections);
                      }}
                    />
                    {sections.length > 1 && (
                      <Button onClick={() => removeSection(sIndex)} icon={DeleteMinor} tone="critical" />
                    )}
                  </InlineStack>

                  <div style={{ marginTop: '1rem' }}>
                    {section.rows.map((row, rIndex) => (
                      <div key={rIndex} style={{ marginBottom: '1rem' }}>
                        <FormLayout>
                          <FormLayout.Group condensed>
                            <TextField
                              label="ID"
                              value={row.id}
                              onChange={(value) => updateRow(sIndex, rIndex, 'id', value)}
                              placeholder="option_1"
                              autoComplete="off"
                            />
                            <TextField
                              label="Title"
                              value={row.title}
                              onChange={(value) => updateRow(sIndex, rIndex, 'title', value)}
                              placeholder="Option Title"
                              autoComplete="off"
                            />
                          </FormLayout.Group>
                          <TextField
                            label="Description (optional)"
                            value={row.description}
                            onChange={(value) => updateRow(sIndex, rIndex, 'description', value)}
                            placeholder="Optional description"
                            autoComplete="off"
                          />
                        </FormLayout>
                      </div>
                    ))}
                    <Button onClick={() => addRow(sIndex)} size="slim">Add Row</Button>
                  </div>
                </Card>
              ))}
            </div>
          </>
        );

      case 'buttons':
        return (
          <>
            <Banner tone="info">
              <p>Maximum 3 buttons allowed. Button titles max 20 characters.</p>
            </Banner>
            {buttons.map((button, index) => (
              <FormLayout.Group key={index}>
                <TextField
                  label={`Button ${index + 1} ID`}
                  value={button.id}
                  onChange={(value) => updateButton(index, 'id', value)}
                  placeholder="btn_confirm"
                  autoComplete="off"
                />
                <TextField
                  label={`Button ${index + 1} Title`}
                  value={button.title}
                  onChange={(value) => updateButton(index, 'title', value.substring(0, 20))}
                  placeholder="Confirm"
                  maxLength={20}
                  autoComplete="off"
                />
                {buttons.length > 1 && (
                  <Button onClick={() => removeButton(index)} icon={DeleteMinor} tone="critical" />
                )}
              </FormLayout.Group>
            ))}
            {buttons.length < 3 && (
              <Button onClick={addButton} icon={PlusMinor}>Add Button</Button>
            )}
          </>
        );

      case 'cta':
        return (
          <>
            <Banner tone="info">
              <p>Maximum 2 call-to-action buttons allowed.</p>
            </Banner>
            {ctaButtons.map((button, index) => (
              <Card key={index}>
                <InlineStack gap="400">
                  <Select
                    label="Button Type"
                    options={[
                      { label: 'URL/Website', value: 'url' },
                      { label: 'Phone Number', value: 'phone_number' },
                    ]}
                    value={button.type}
                    onChange={(value) => updateCtaButton(index, 'type', value)}
                  />
                  {ctaButtons.length > 1 && (
                    <Button onClick={() => removeCtaButton(index)} icon={DeleteMinor} tone="critical" />
                  )}
                </InlineStack>
                <TextField
                  label="Button Text"
                  value={button.text}
                  onChange={(value) => updateCtaButton(index, 'text', value)}
                  placeholder="Visit Website"
                  autoComplete="off"
                />
                {button.type === 'url' ? (
                  <TextField
                    label="URL"
                    value={button.url}
                    onChange={(value) => updateCtaButton(index, 'url', value)}
                    placeholder="https://example.com"
                    type="url"
                    autoComplete="url"
                  />
                ) : (
                  <TextField
                    label="Phone Number"
                    value={button.phone_number}
                    onChange={(value) => updateCtaButton(index, 'phone_number', value)}
                    placeholder="+1234567890"
                    type="tel"
                    autoComplete="tel"
                  />
                )}
              </Card>
            ))}
            {ctaButtons.length < 2 && (
              <Button onClick={addCtaButton} icon={PlusMinor}>Add Button</Button>
            )}
          </>
        );

      case 'product':
        return (
          <>
            <TextField
              label="Catalog ID"
              value={catalogId}
              onChange={setCatalogId}
              placeholder="Your WhatsApp Catalog ID"
              helpText="Get this from Facebook Catalog Manager"
              autoComplete="off"
            />
            <TextField
              label="Product Retailer ID"
              value={productRetailerId}
              onChange={setProductRetailerId}
              placeholder="product-123"
              helpText="Your product's unique identifier"
              autoComplete="off"
            />
          </>
        );

      case 'product_list':
        return (
          <>
            <TextField
              label="Catalog ID"
              value={catalogId}
              onChange={setCatalogId}
              placeholder="Your WhatsApp Catalog ID"
              autoComplete="off"
            />
            {productSections.map((section, index) => (
              <Card key={index}>
                <TextField
                  label="Section Title"
                  value={section.title}
                  autoComplete="off"
                  onChange={(value) => {
                    const newSections = [...productSections];
                    newSections[index].title = value;
                    setProductSections(newSections);
                  }}
                />
                {section.product_items.map((item, itemIndex) => (
                  <TextField
                    key={itemIndex}
                    label={`Product ${itemIndex + 1} ID`}
                    value={item.product_retailer_id}
                    autoComplete="off"
                    onChange={(value) => {
                      const newSections = [...productSections];
                      newSections[index].product_items[itemIndex].product_retailer_id = value;
                      setProductSections(newSections);
                    }}
                    placeholder="product-123"
                  />
                ))}
              </Card>
            ))}
          </>
        );

      case 'flow':
        return (
          <>
            <TextField
              label="Flow ID"
              value={flowId}
              onChange={setFlowId}
              placeholder="your-flow-id"
              helpText="WhatsApp Flow ID from Flow Builder"
              autoComplete="off"
            />
            <TextField
              label="Flow CTA Text"
              value={flowCta}
              onChange={setFlowCta}
              placeholder="Fill Form"
              helpText="Text shown on the flow button"
              autoComplete="off"
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Card>
        <BlockStack gap="500">
          {/* AI Features Banner */}
          {(products.length > 0 || conversationId) && (
            <Banner tone="info">
              <BlockStack gap="400">
                <p style={{ fontWeight: 'bold' }}>✨ AI-Powered Features Available</p>
                <ButtonGroup>
                  {products.length > 0 && (
                    <Button
                      icon={MagicMinor}
                      onClick={() => setAiGeneratorModalActive(true)}
                    >
                      AI Generate Message
                    </Button>
                  )}
                  {conversationId && (
                    <Button
                      loading={aiSuggestionLoading}
                      onClick={getAiSuggestion}
                    >
                      Get AI Suggestion
                    </Button>
                  )}
                </ButtonGroup>
              </BlockStack>
            </Banner>
          )}

          {/* AI Suggestion Display */}
          {showAiSuggestion && aiSuggestion && (
            <Banner
              tone="success"
              action={{
                content: 'Use Suggestion',
                onAction: applySuggestion,
              }}
              secondaryAction={{
                content: 'Dismiss',
                onAction: () => setShowAiSuggestion(false),
              }}
            >
              <BlockStack gap="400">
                <p><strong>AI Recommends:</strong> {aiSuggestion.suggestedType}</p>
                <p style={{ fontSize: '0.9rem' }}>{aiSuggestion.reasoning}</p>
                <InlineStack gap="200">
                  <Badge tone={aiSuggestion.confidence >= 0.7 ? 'success' : 'warning'}>
                    {`${(aiSuggestion.confidence * 100).toFixed(0)}% confidence`}
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Banner>
          )}

          <FormLayout>
            <Select
              label="Message Type"
              options={messageTypeOptions}
              value={messageType}
              onChange={(value) => setMessageType(value as MessageType)}
            />

          <TextField
            label="Recipient Phone Number"
            value={to}
            onChange={setTo}
            placeholder="+1234567890"
            type="tel"
            helpText="Include country code"
            autoComplete="tel"
          />

          <Divider />

          <TextField
            label="Header (optional)"
            value={header}
            onChange={setHeader}
            placeholder="Special Offer!"
            autoComplete="off"
          />

          <TextField
            label="Body Text"
            value={body}
            onChange={setBody}
            placeholder="Your message here..."
            multiline={4}
            helpText="Main message content"
            autoComplete="off"
          />

          <TextField
            label="Footer (optional)"
            value={footer}
            onChange={setFooter}
            placeholder="Powered by WhatsApp CRM"
            autoComplete="off"
          />

          <Divider />

          {renderMessageTypeFields()}

          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          <ButtonGroup>
            <Button variant="primary" loading={loading} onClick={handleSend}>
              Send Message
            </Button>
            <Button onClick={() => {
              setTo('');
              setHeader('');
              setBody('');
              setFooter('');
              setError('');
            }}>
              Clear
            </Button>
          </ButtonGroup>
        </FormLayout>
        </BlockStack>

        <BlockStack gap="400">
          <h3>Preview</h3>
          <div style={{
          padding: '1rem',
          background: '#f4f6f8',
          borderRadius: '8px',
          maxWidth: '350px'
        }}>
          {header && <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{header}</p>}
          <p style={{ marginBottom: '0.5rem' }}>{body || 'Your message will appear here...'}</p>
          {footer && <p style={{ fontSize: '0.85rem', color: '#666' }}>{footer}</p>}

          <div style={{ marginTop: '1rem' }}>
            {messageType === 'list' && (
              <div style={{
                padding: '0.75rem',
                background: 'white',
                borderRadius: '4px',
                textAlign: 'center',
                border: '1px solid #ddd'
              }}>
                📋 {buttonText}
              </div>
            )}
            {messageType === 'buttons' && (
              <BlockStack gap="200">
                {buttons.filter(b => b.title).map((btn, i) => (
                  <div key={i} style={{
                    padding: '0.5rem',
                    background: 'white',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid #ddd'
                  }}>
                    {btn.title}
                  </div>
                ))}
              </BlockStack>
            )}
          </div>
        </div>
        </BlockStack>
      </Card>

    {/* AI Message Generator Modal */}
    <Modal
      open={aiGeneratorModalActive}
      onClose={() => setAiGeneratorModalActive(false)}
      title="AI Message Generator"
    >
      <Modal.Section>
        <AIMessageGenerator
          products={products}
          customerPhone={to}
          onMessageGenerated={handleAiGeneratedMessage}
        />
      </Modal.Section>
    </Modal>
  </>
  );
};
